import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CallParticipant, CallTransport, NativeTransportContextValue, TransportEvent } from 'pages/Meetings/call/custom/callTransport';
import { nativeCall, type NativeCallState, type NativeListener } from './nativeBridge';
import { PIP_ERROR } from './useNativePictureInPicture';
import { nativeObservationKey } from './nativeBreakoutObservation';
import { sameNativeCallScope } from './nativeVideoViewport';

type RetainedUserData = { meetingId: string; generation: number; participantId: string; value: unknown };
// The native SDK permits one active media call. Keep one bounded slot across
// route unmounts; only trusted native snapshots can select or retire its scope.
let retainedUserData: RetainedUserData | undefined;

/** One native media session; snapshots contain metadata only. No browser Daily object exists. */
export function useNativeCallSession(meetingId: string, initial?: NativeCallState) {
  const [state, setState] = useState<NativeCallState>(initial?.meetingId === meetingId ? initial : { state: 'idle' });
  const [historicalTerminal, setHistoricalTerminal] = useState(false);
  const [error, setError] = useState('');
  const [snapshotError, setSnapshotError] = useState('');
  const dismissError = useCallback(() => { setError(''); setSnapshotError(''); }, []);
  const stateRef = useRef(state);
  const ownershipRevision = useRef(0);
  const observationRevision = useRef(0);
  const [snapshotRetry, setSnapshotRetry] = useState(0);
  const retrySnapshot = useCallback(() => setSnapshotRetry(value => value + 1), []);
  const [snapshotSettled, setSnapshotSettled] = useState(Boolean(initial?.meetingId === meetingId));
  const mounted = useRef(true);
  const listeners = useRef(new Map<string, Set<(event: TransportEvent) => void>>());
  const [userData, setUserData] = useState<RetainedUserData>();
  const applyState = useCallback((next: NativeCallState, fromInitialSnapshot = false) => {
    if (!mounted.current) return;
    if (!next || !['idle', 'preview', 'joining', 'joined', 'waiting', 'left', 'denied', 'error'].includes(next.state)) { setSnapshotError('Unable to verify the active call. Retry or wait for a native update.'); return; }
    if (next.participants && (typeof next.participants !== 'object' || Array.isArray(next.participants)
      || Object.values(next.participants).some(person => !person || typeof person !== 'object'))) {
      setSnapshotError('Unable to verify the active call. Retry or wait for a native update.'); return;
    }
    setSnapshotSettled(true);
    setSnapshotError('');
    if (next.meetingId && next.meetingId.toLowerCase() !== meetingId.toLowerCase()) {
      if (['preview', 'joining', 'joined', 'waiting'].includes(next.state)) setError('Another meeting is already open. Return to it and leave before joining this meeting.');
      return;
    }
    if (next.generation !== undefined && stateRef.current.generation !== undefined && next.generation < stateRef.current.generation) return;
    // A delayed SDK/command snapshot cannot resurrect a retired peer. Only a
    // newer native generation (created after fresh admission) can become active.
    if (next.generation === stateRef.current.generation && ['left', 'denied', 'error'].includes(stateRef.current.state)
      && ['preview', 'joining', 'joined', 'waiting'].includes(next.state)) return;
    if (retainedUserData && (['idle', 'left', 'denied', 'error'].includes(next.state)
      || (next.meetingId && next.meetingId !== retainedUserData.meetingId)
      || (next.generation !== undefined && next.generation !== retainedUserData.generation)
      || (next.participantId && next.participantId !== retainedUserData.participantId))) retainedUserData = undefined;
    setUserData(retainedUserData && next.meetingId === retainedUserData.meetingId && next.generation === retainedUserData.generation
      ? retainedUserData : undefined);
    // Record every accepted ownership transition before React can batch away
    // an intermediate pause. Metadata-only snapshots keep the same revision.
    if (!sameNativeCallScope(stateRef.current, next)) ownershipRevision.current += 1;
    if (nativeObservationKey(stateRef.current) !== nativeObservationKey(next)) observationRevision.current += 1;
    stateRef.current = next;
    setHistoricalTerminal(fromInitialSnapshot && ['left', 'denied', 'error'].includes(next.state));
    setState({ ...next });
  }, [meetingId]);
  const subscribe = useCallback((type: string, listener: (event: TransportEvent) => void) => {
    const group = listeners.current.get(type) || new Set();
    group.add(listener); listeners.current.set(type, group);
    return () => { group.delete(listener); };
  }, []);
  useEffect(() => {
    mounted.current = true;
    let active = true;
    let events = 0;
    const handles: NativeListener[] = [];
    const attach = (promise: Promise<NativeListener>) => {
      void promise.then(handle => { if (active) handles.push(handle); else void handle.remove(); }).catch(() => {
        if (active) setError('The native call controls are unavailable. Restart the app to try again.');
      });
    };
    const registration = nativeCall.listen(next => { if (active) { events += 1; applyState(next); } });
    attach(registration);
    attach(nativeCall.events(event => {
      if (!active || event.meetingId?.toLowerCase() !== meetingId.toLowerCase() || event.generation !== stateRef.current.generation) return;
      listeners.current.get(event.type)?.forEach(listener => listener(event));
    }));
    const snapshot = events;
    const deadline = window.setTimeout(() => {
      if (active && events === snapshot) setSnapshotError('Unable to read the active call. Retry or wait for a native update.');
    }, 8000);
    void registration.then(() => active ? nativeCall.getState() : null).then(next => { if (active && next && events === snapshot) applyState(next, true); }).catch(() => {
      if (active) setSnapshotError('Unable to read the active call. Retry or wait for a native update.');
    }).finally(() => window.clearTimeout(deadline));
    return () => {
      window.clearTimeout(deadline);
      active = false; mounted.current = false;
      handles.forEach(handle => void handle.remove().catch(() => undefined));
      if (stateRef.current.state === 'preview') void nativeCall.leave(stateRef.current).catch(() => undefined);
      // Navigation detaches the UI, not the user's ongoing native call.
    };
  }, [applyState, meetingId, snapshotRetry]);
  const command = useCallback(async (name: string, args: Record<string, unknown> = {}) => {
    const captured = stateRef.current;
    const capturedRevision = observationRevision.current;
    if (captured.accessVerifying) throw new Error('Meeting access is being verified. Call controls are temporarily paused.');
    try {
      const result = await nativeCall.command(captured, name, args);
      // A room command resolves before SDK joined on both native platforms.
      // Only native events/getState establish the new peer, never this acknowledgment.
      if (name !== 'switchRoom' && capturedRevision === observationRevision.current && result?.state && captured.meetingId?.toLowerCase() === stateRef.current.meetingId?.toLowerCase()) applyState(result);
      return result;
    } catch (failure) {
      if (mounted.current && captured.generation === stateRef.current.generation) setError(name === 'enterPictureInPicture' ? PIP_ERROR : 'That call action could not complete. Check your connection and try again.');
      throw failure;
    }
  }, [applyState]);
  const dispatch = useCallback((name: string, args: Record<string, unknown> = {}) => { void command(name, args).catch(() => undefined); }, [command]);
  const participants = useMemo(() => {
    const people: Record<string, CallParticipant> = {};
    Object.values(state.participants || {}).forEach(person => {
      if (!person || typeof person !== 'object' || !person.session_id) return;
      const retained = person.local && userData && userData.meetingId === state.meetingId && userData.generation === state.generation && userData.participantId === person.session_id ? userData.value : undefined;
      if (person.local && state.handStateRetention) {
        // Upgraded shells persist the local hand before publishing its revision.
        // Native metadata wins over an optimistic/legacy JS overlay on reload.
        const extra = retained && typeof retained === 'object' ? retained : {};
        const canonical = person.userData && typeof person.userData === 'object' ? person.userData : {};
        people[person.session_id] = { ...person, userData: { ...extra, ...canonical } };
      } else people[person.session_id] = retained !== undefined ? { ...person, userData: retained } : person;
    });
    return people;
  }, [state.participants, state.meetingId, state.generation, state.handStateRetention, userData]);
  const participantsRef = useRef(participants); participantsRef.current = participants;
  const client = useMemo<CallTransport>(() => ({
    kind: 'native',
    // Retained callbacks must not act on the next room before React commits its
    // roster. The client identity also scopes shared tool state to this peer.
    meetingState: () => mounted.current && stateRef.current.generation === state.generation && stateRef.current.state === 'joined' && !stateRef.current.accessVerifying ? 'joined-meeting' : 'joining-meeting',
    participants: () => {
      const people = participantsRef.current;
      const local = Object.values(people).find(person => person.local);
      return local ? { ...people, local } : people;
    },
    sendAppMessage: (data, to = '*') => dispatch('sendAppMessage', { data, to }),
    setUserData: value => {
      const scope = stateRef.current;
      if (!mounted.current || scope.generation !== state.generation
        || scope.state !== 'joined' || scope.accessVerifying) return;
      if (!scope.meetingId || scope.generation === undefined || !scope.participantId) return;
      retainedUserData = { meetingId: scope.meetingId, generation: scope.generation, participantId: scope.participantId, value };
      setUserData(retainedUserData);
    },
    setLocalAudio: enabled => dispatch('setLocalAudio', { enabled }),
    setLocalVideo: enabled => dispatch('setLocalVideo', { enabled }),
    updateParticipant: (sessionId, changes) => dispatch('updateParticipant', { sessionId, ...changes }),
    updateParticipants: changes => Object.entries(changes).forEach(([sessionId, change]) => dispatch('updateParticipant', { sessionId, ...change })),
    startTranscription: () => { void nativeCall.command(stateRef.current, 'startTranscription').catch(() => undefined); },
  }), [dispatch, state.generation]);
  // Publish state-only changes too: shared tools must observe joining and
  // verification ending even when the provider roster remains identical.
  const transport = useMemo<NativeTransportContextValue>(() => ({
    client, participants, localSessionId: state.participantId || '', isTranscribing: Boolean(state.transcribing), subscribe,
  }), [client, participants, state, subscribe]);
  return { state, stateRef, ownershipRevision, observationRevision, snapshotSettled, retrySnapshot, historicalTerminal, applyState, transport, command, error: error || snapshotError, setError, dismissError };
}
